import {
  ActionValidation,
  BooleanValidation,
  NumberValidation,
  StringValidation,
} from '../../../../utils/component-validations';
import { HIDDEN_SELECTOR } from '../../../../utils/keyboard-nav-support';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { or } from 'ember-truth-helpers';
import { tracked } from '@glimmer/tracking';
import Button from '../../core/button.gjs';
import FlexRow from '../../layout/flex/row.gjs';
import InputBase from '../../core/input.gjs';
import MultiselectClearFilter from '../../core/multiselect-clear-filter.gjs';
import ShiftFocusToParent from '../../../../modifiers/shift-focus-to-parent';
import Super from '../../core/base.gjs';
import styles from './trigger.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

export default class SpdsInputSearchTrigger extends Super {
  arguments = [
    new ActionValidation({
      optional: [
        'onClearInput',
        'onInput',
        'registerClearFilterComponent',
        'unregisterClearFilterComponent',
      ],
      required: ['onClose', 'openMenu'],
    }),
    new BooleanValidation({ optional: ['disabled', 'error', 'multiselect', 'required'] }),
    new NumberValidation({ required: ['menuOpenThreshold'] }),
    new StringValidation({ optional: ['id', 'placeholder', 'value'] }),
  ];
  baseClass = 'spds-input-search-trigger';
  classesToMapToArgs = ['disabled', 'error'];
  clearBtnSelector = 'spds-input-search-clear-btn';
  componentName = 'Spds::Input::Search::Trigger';
  elementsToRegister = [
    { name: 'clearButton', selector: this.clearBtnSelector },
    { name: 'trigger', selector: 'input' },
  ];
  placeholderDefault = 'Placeholder';
  styles = styles;

  @tracked clearButton;
  @tracked clearFilterComponent;

  get clearButtonClasses() {
    let classes = [this.clearBtnSelector];

    if (this.hideClearButton) classes.push(HIDDEN_SELECTOR);

    return classes.join(' ');
  }

  get clearButtonId() {
    return this.composeSubcomponentId('clear-button');
  }

  get hasSelectedOption() {
    let { multiselect, selected } = this.args;

    return !multiselect && selected;
  }

  get hideClearButton() {
    return this.args.value?.length <= 0;
  }

  get inputId() {
    return this.composeSubcomponentId('input');
  }

  get placeholder() {
    return this.args.placeholder ?? this.placeholderDefault;
  }

  get shouldShowFilter() {
    let { multiselect, selected } = this.args;

    return multiselect && Array.isArray(selected) && selected.length > 0;
  }

  onInput = evt => {
    if (evt.newValue) {
      evt.target.value = evt.newValue;
    }

    this.toggleMenu();
  };

  toggleMenu = () => {
    let { menuOpenThreshold } = this.args;

    if (this.trigger.value.length >= menuOpenThreshold && !this.args.open) {
      this.args.openMenu();
    } else if (this.trigger.value.length < menuOpenThreshold && this.args.open) {
      this.args.closeMenu();
    }
  };

  <template>
    <FlexRow
      @alignItems="center"
      @gap="10px"
      @justifyContent="center"
      class={{this.classes}}
      data-test-spds-input-search-input-container
      id={{this.id}}
      {{this.setupComponent}}
      {{this.validateArguments}}
      ...attributes
    >
      {{#if this.shouldShowFilter}}
        <MultiselectClearFilter
          @disabled={{@disabled}}
          @clearSelection={{@clearSelection}}
          @registerComponent={{fn this.registerComponent "clearFilterComponent"}}
          @selected={{@selected}}
          @unregisterComponent={{fn this.unregisterComponent "clearFilterComponent"}}
          data-test-spds-input-search-multiselect-clear
        />
      {{else}}
        {{svgJar (or @searchIcon "search") class="spds-input-search-icon"}}
      {{/if}}

      <InputBase
        @disabled={{@disabled}}
        @id={{this.inputId}}
        @noBorder={{true}}
        @onInput={{@onInput}}
        @placeholder={{this.placeholder}}
        @required={{@required}}
        @type="text"
        @value={{@value}}
        class="spds-input-search-input"
        data-test-spds-input-search-input
        {{on "click" this.toggleMenu}}
        {{on "input" this.onInput}}
        {{ShiftFocusToParent}}
      />

      <Button
        @alignItems="center"
        class={{this.clearButtonClasses}}
        data-test-spds-input-search-clear-button
        id={{this.clearButtonId}}
        tabindex="-1"
        {{on "click" @clearInput}}
      >
        {{svgJar "x-mark" class="spds-input-search-clear-btn-icon"}}
      </Button>
    </FlexRow>
  </template>
}
